<template>
    <div id="content">
        <div class="page-wrap">
            <router-link to="/spheres" class="back-link">
                <i class="bi bi-chevron-left"></i>
                {{ $t('spheres.new.backToList') }}
            </router-link>

            <div class="form-card">
                <div class="form-card-header">
                    <div class="form-card-header-icon">
                        <i class="bi bi-collection"></i>
                    </div>
                    <div>
                        <h2 class="form-card-title">{{ $t('spheres.new.title') }}</h2>
                        <p class="form-card-subtitle">{{ $t('spheres.new.subtitle') }}</p>
                    </div>
                </div>

                <form @submit.prevent="submit" class="form-card-body">
                    <!-- Name -->
                    <div class="field">
                        <label for="sphereName" class="field-label">
                            <i class="bi bi-pencil-square"></i>
                            {{ $t('spheres.new.fields.name.label') }}
                        </label>
                        <input
                            type="text"
                            class="field-input"
                            id="sphereName"
                            v-model="form.name"
                            :placeholder="$t('spheres.new.fields.name.placeholder')"
                            maxlength="127"
                            required
                        />
                    </div>

                    <!-- Slug -->
                    <div class="field">
                        <label for="sphereSlug" class="field-label">
                            <i class="bi bi-link-45deg"></i>
                            {{ $t('spheres.new.fields.slug.label') }}
                        </label>
                        <input
                            type="text"
                            class="field-input field-input-mono"
                            id="sphereSlug"
                            v-model="form.slug"
                            :placeholder="defaultSlug || $t('spheres.new.fields.slug.placeholderAuto')"
                            pattern="[a-z0-9-]+"
                            maxlength="64"
                        />
                        <div class="field-hint">
                            {{ $t('spheres.new.fields.slug.hint') }}
                        </div>
                    </div>

                    <!-- Description -->
                    <div class="field">
                        <label for="sphereDescription" class="field-label">
                            <i class="bi bi-card-text"></i>
                            {{ $t('spheres.new.fields.description.label') }}
                            <span class="field-label-meta">{{ $t('spheres.new.fields.description.optional') }}</span>
                        </label>
                        <textarea
                            class="field-input"
                            id="sphereDescription"
                            v-model="form.description"
                            rows="2"
                            maxlength="255"
                            :placeholder="$t('spheres.new.fields.description.placeholder')"
                        ></textarea>
                    </div>

                    <!-- Object labels -->
                    <div class="field-row">
                        <div class="field">
                            <label for="objSing" class="field-label">
                                <i class="bi bi-box"></i>
                                {{ $t('spheres.new.fields.objectSingular.label') }}
                            </label>
                            <input
                                type="text"
                                class="field-input"
                                id="objSing"
                                v-model="form.object_label_singular"
                                :placeholder="$t('spheres.new.fields.objectSingular.placeholder')"
                                maxlength="64"
                                required
                            />
                        </div>
                        <div class="field">
                            <label for="objPlural" class="field-label">
                                <i class="bi bi-boxes"></i>
                                {{ $t('spheres.new.fields.objectPlural.label') }}
                            </label>
                            <input
                                type="text"
                                class="field-input"
                                id="objPlural"
                                v-model="form.object_label_plural"
                                :placeholder="$t('spheres.new.fields.objectPlural.placeholder')"
                                maxlength="64"
                                required
                            />
                        </div>
                    </div>

                    <!-- Icon -->
                    <div class="field">
                        <label class="field-label">
                            <i class="bi bi-image"></i>
                            {{ $t('spheres.new.fields.icon.label') }}
                        </label>
                        <IconPicker v-model="iconModel" />
                    </div>

                    <!-- Position -->
                    <div class="field field-narrow">
                        <label for="position" class="field-label">
                            <i class="bi bi-arrow-down-up"></i>
                            {{ $t('spheres.new.fields.position.label') }}
                        </label>
                        <input
                            type="number"
                            class="field-input"
                            id="position"
                            v-model.number="form.position"
                            min="0"
                        />
                    </div>

                    <!-- Footer -->
                    <div class="form-footer">
                        <button
                            type="button"
                            class="btn-secondary"
                            @click="$router.push('/spheres')"
                            :disabled="submitting"
                        >
                            {{ $t('spheres.new.actions.cancel') }}
                        </button>
                        <button type="submit" class="btn-primary" :disabled="submitting">
                            <i v-if="submitting" class="bi bi-arrow-repeat spin"></i>
                            <i v-else class="bi bi-check2"></i>
                            {{ submitting ? $t('spheres.new.actions.submitting') : $t('spheres.new.actions.submit') }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import cyrillicToTranslit from 'cyrillic-to-translit-js'
import IconPicker from './IconPicker.vue'

export default {
    name: 'NewSphere',
    components: { IconPicker },
    data: () => ({
        form: {
            name: '',
            slug: '',
            description: '',
            object_label_singular: '',
            object_label_plural: '',
            position: 0
        },
        iconModel: { icon_slug: null, icon_url: null, preview_url: null },
        submitting: false
    }),
    mounted() {
        this.$emit('setPageTitle', this.$t('spheres.new.pageTitle'))
    },
    computed: {
        defaultSlug() {
            if (!this.form.name) return ''
            return new cyrillicToTranslit().transform(this.form.name, '-').toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '')
        }
    },
    methods: {
        async submit() {
            if (!this.iconModel.icon_slug && !this.iconModel.icon_url) {
                this.$notify({ text: this.$t('spheres.new.notify.selectIcon'), type: 'error' })
                return
            }
            this.submitting = true
            const data = { ...this.form }
            if (this.iconModel.icon_slug) data.icon_slug = this.iconModel.icon_slug
            if (this.iconModel.icon_url) data.icon_url = this.iconModel.icon_url
            if (!data.slug) delete data.slug
            if (!data.description) delete data.description
            const r = await this.$api.createSphere(data)
            this.submitting = false
            if (r.result === 'SUCCESS') {
                this.$notify({ text: this.$t('spheres.new.notify.created'), type: 'success' })
                this.$router.push('/spheres/' + r.data.id)
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        }
    }
}
</script>

<style scoped>
.page-wrap {
    max-width: 760px;
    margin: 0 auto;
    padding: 24px 0;
}

.back-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    margin-bottom: 16px;
    transition: color 0.15s ease;
}
.back-link:hover {
    color: #7c3aed;
}
.back-link i {
    font-size: 14px;
}

/* Card */
.form-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}
.form-card-header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #f1f5f9;
}
.form-card-header-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #f5f0ff;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
}
.form-card-title {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px 0;
}
.form-card-subtitle {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}

.form-card-body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* Fields */
.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.field-narrow {
    max-width: 240px;
}
.field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}
@media (max-width: 600px) {
    .field-row {
        grid-template-columns: 1fr;
    }
}

.field-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
}
.field-label i {
    font-size: 14px;
    color: #7c3aed;
}
.field-label-meta {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
    color: #94a3b8;
    margin-left: 2px;
}

.field-input {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    color: #1e293b;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    font-family: inherit;
}
.field-input::placeholder {
    color: #94a3b8;
}
.field-input:hover {
    border-color: #cbd5e1;
}
.field-input:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}
.field-input-mono {
    font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 13px;
}
textarea.field-input {
    resize: vertical;
    min-height: 60px;
}

.field-hint {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 2px;
}

/* Footer */
.form-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
    margin-top: 4px;
}
.btn-primary,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 18px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1px solid transparent;
}
.btn-primary {
    background: #7c3aed;
    border-color: #7c3aed;
    color: #fff;
    box-shadow: 0 1px 2px rgba(124, 58, 237, 0.2);
}
.btn-primary:hover:not(:disabled) {
    background: #6d28d9;
    border-color: #6d28d9;
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
}
.btn-primary:disabled {
    background: #c4b5fd;
    border-color: #c4b5fd;
    cursor: not-allowed;
    box-shadow: none;
}
.btn-secondary {
    background: #fff;
    border-color: #e5e7eb;
    color: #475569;
}
.btn-secondary:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #1e293b;
}
.btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.spin {
    animation: ns-spin 1s linear infinite;
}
@keyframes ns-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>
